import Jaws from '@modules/three/common/Jaws.ts';
import JawCorrection from '@correction/three/JawCorrection';
import ToothCorrection from '@correction/three/ToothCorrection';
import OcclusionContactMap from '@correction/three/OcclusionContactMap';
import { toggleVisibility } from '@/modules/three/common/toggleVisibility';
import { JawsCorrectionCreate } from '@/schema/JawsCorrection';

export type JawsCorrectionUploadPayload = {
  data: JawsCorrectionCreate;
  lowerJawModel: Blob;
  upperJawModel: Blob;
};

export default class JawsCorrection extends Jaws {
  public lowerJaw: JawCorrection;
  public upperJaw: JawCorrection;
  public activeTooth: ToothCorrection | null = null;
  private updateStore: () => void = null!;
  private readonly occlusionContactMap = new OcclusionContactMap(this);

  constructor(lowerJaw: JawCorrection, upperJaw: JawCorrection) {
    super(lowerJaw, upperJaw);
    lowerJaw.setJawsCorrection(this);
    upperJaw.setJawsCorrection(this);
    this.lowerJaw = lowerJaw;
    this.upperJaw = upperJaw;
  }

  public setActiveTooth(tooth: ToothCorrection | null) {
    const selectionChanged = tooth !== this.activeTooth;
    if (selectionChanged) {
      this.lowerJaw.clearContactOverlay();
      this.upperJaw.clearContactOverlay();
    }
    this.activeTooth = tooth;
    this.updateStore();
  }

  public setUpdateJawsStore(updateStore: () => void) {
    this.updateStore = updateStore;
  }

  public getOcclusionContactMap(): OcclusionContactMap {
    return this.occlusionContactMap;
  }

  public setOcclusionMapEnabled(enabled: boolean) {
    this.occlusionContactMap.setEnabled(enabled);
  }

  /** Shows/hides the initial-pose ghost teeth on both jaws. */
  public setInitialPoseGhostsVisible(visible: boolean) {
    this.upperJaw.setInitialPoseGhostsVisible(visible);
    this.lowerJaw.setInitialPoseGhostsVisible(visible);
  }

  public toggleVisibility(jaw: 'lower' | 'upper', visible: boolean) {
      if (jaw === 'lower') {
        toggleVisibility(this.lowerJaw, visible);
        toggleVisibility(this.lowerJaw.teeth, visible);
      } else {
        toggleVisibility(this.upperJaw, visible);
        toggleVisibility(this.upperJaw.teeth, visible);
      }
    }

  /**
   * Collects upload-ready DTOs and meshes for both jaws.
   */
  public async buildCorrectionUploadPayload(scanId: string, treatmentWeek: number): Promise<JawsCorrectionUploadPayload> {
    const [lowerBundle, upperBundle] = await Promise.all([
      this.lowerJaw.toCorrectionUploadBundle(treatmentWeek),
      this.upperJaw.toCorrectionUploadBundle(treatmentWeek),
    ]);

    return {
      data: {
        scanId,
        treatmentWeek,
        lowerJawData: lowerBundle.data,
        upperJawData: upperBundle.data,
      },
      lowerJawModel: lowerBundle.model,
      upperJawModel: upperBundle.model,
    };
  }
}
